import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ShoppingBag, Trash2 } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { tint } from "@/lib/mall";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const LABEL = {
    kiosk: "Starter",
    storefront: "Storefront",
    department: "Pro Shop",
};

/** Drawn bags — thin plastic for a kiosk, a shop bag for a storefront, a luxury bag for a Pro Shop. */
export function BagArt({ tier, theme, size = 132 }) {
    const p = theme?.primary || "#FFFFFF";
    const s = theme?.on || "#FFFFFF";
    const w = size;
    const h = Math.round(size * 1.1);

    if (tier === "kiosk") {
        return (
            <svg width={w} height={h} viewBox="0 0 120 132" aria-hidden="true">
                <path d="M28 34 L92 34 L100 122 Q60 132 20 122 Z" fill={tint(p, 0.16)} stroke={p} strokeWidth="1.5" strokeDasharray="4 3" />
                <path d="M40 34 Q40 12 60 12 Q80 12 80 34" fill="none" stroke={p} strokeWidth="1.5" strokeDasharray="4 3" />
                <path d="M30 52 Q60 60 90 52" fill="none" stroke={tint(p, 0.5)} strokeWidth="1" />
                <path d="M26 90 Q60 100 96 90" fill="none" stroke={tint(p, 0.35)} strokeWidth="1" />
            </svg>
        );
    }
    if (tier === "department") {
        return (
            <svg width={w} height={h} viewBox="0 0 120 132" aria-hidden="true">
                <rect x="16" y="34" width="88" height="90" rx="5" fill={p} />
                <rect x="16" y="34" width="88" height="90" rx="5" fill="none" stroke={tint(p, 0.9)} strokeWidth="2" />
                <rect x="16" y="34" width="88" height="13" fill={tint(s, 0.25)} />
                <path d="M40 34 Q40 8 60 8 Q80 8 80 34" fill="none" stroke={s} strokeWidth="3.5" strokeLinecap="round" />
                <rect x="30" y="62" width="60" height="34" rx="3" fill={tint(s, 0.16)} />
                <text x="60" y="84" textAnchor="middle" fontSize="13" fontWeight="900" fill={s} fontFamily="Cabinet Grotesk, Outfit, sans-serif">13</text>
            </svg>
        );
    }
    return (
        <svg width={w} height={h} viewBox="0 0 120 132" aria-hidden="true">
            <rect x="20" y="36" width="80" height="86" rx="3" fill={tint(p, 0.22)} stroke={p} strokeWidth="2" />
            <rect x="20" y="36" width="80" height="10" fill={tint(p, 0.45)} />
            <path d="M42 36 Q42 14 60 14 Q78 14 78 36" fill="none" stroke={p} strokeWidth="2.5" strokeLinecap="round" />
            <path d="M20 74 L100 74" stroke={tint(p, 0.3)} strokeWidth="1" />
        </svg>
    );
}

/** One shop's items from the cart. Click it and you check out with that shop only. */
export default function Bag({ bag, onRemove, expanded = false }) {
    const navigate = useNavigate();
    const { theme, tier } = bag;

    return (
        <div className="flex flex-col overflow-hidden rounded-3xl border" data-testid={`bag-${bag.slug}`}
            style={{ borderColor: tint(theme.primary, 0.35), background: `linear-gradient(160deg, ${tint(theme.primary, 0.1)}, rgba(0,0,0,0))` }}>
            <div className="flex items-start gap-4 p-5">
                <span className="shrink-0" style={{ filter: `drop-shadow(0 10px 24px ${tint(theme.primary, 0.35)})` }}>
                    <BagArt tier={tier} theme={theme} size={96} />
                </span>
                <div className="min-w-0 flex-1">
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em]" style={{ color: theme.ink || theme.primary }}>{LABEL[tier] || LABEL.storefront}</p>
                    <Link to={`/stores/${bag.slug}`} className="mt-1 block truncate font-display text-lg font-black hover:underline"
                        data-testid={`bag-name-${bag.slug}`}>{bag.name}</Link>
                    <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-400">
                        {bag.count} item{bag.count === 1 ? "" : "s"} · {money(bag.subtotal)}
                    </p>
                    <button onClick={() => navigate(`/checkout/${bag.slug}`)} data-testid={`bag-pay-${bag.slug}`}
                        className="mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wide transition-transform duration-200 hover:scale-[1.03]"
                        style={{ background: theme.primary, color: theme.on }}>
                        <ShoppingBag size={14} /> Check out <ArrowRight size={13} />
                    </button>
                </div>
            </div>

            {expanded && (
                <div className="border-t px-5 py-4" style={{ borderColor: tint(theme.primary, 0.18) }}>
                    <div className="space-y-3">
                        {bag.items.map((it) => (
                            <div key={it.id} className="flex items-center gap-3" data-testid="bag-item">
                                {it.image && <img src={mediaUrl(it.image)} alt="" className="h-11 w-11 shrink-0 rounded-lg object-cover" onError={(e) => (e.target.style.visibility = "hidden")} />}
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-semibold">{it.product_name}</p>
                                    <p className="truncate text-xs text-zinc-500">{it.summary}</p>
                                </div>
                                <span className="font-mono text-sm" style={{ color: theme.ink || theme.primary }}>{money(it.line_total)}</span>
                                {onRemove && (
                                    <button onClick={() => onRemove(it.id)} className="text-zinc-600 hover:text-red-400" data-testid="bag-remove"><Trash2 size={15} /></button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
